import { PhoneAnalyticsTypes } from '@frontend/api-analytics';
import { getHourlyInsightsLabel } from '@frontend/analytics-shared';
import { BarChartData } from '@frontend/charts';
import { formatters } from '@frontend/shared';
import { useFetchAggregatedCallsAndDurationReport } from '../hooks/use-fetch-aggregated-calls-and-duration-report';

type DataKey = 'calls' | 'duration';

type Params = ReturnType<typeof useFetchAggregatedCallsAndDurationReport<PhoneAnalyticsTypes.CallsAggregationKey>> & {
  dataKey: DataKey;
  locations?: Record<string, string>;
};

type ProcessedData = {
  chartData: BarChartData;
  totals: {
    inbound: number;
    outbound: number;
    total: number;
  };
};

export const formatCallVolumeAndDurationCharts = ({
  data,
  dataKey,
  isHourlyData,
  isMultiLocation,
  locations,
}: Params): ProcessedData => {
  let groups: BarChartData['groups'] = [];
  const grandTotals: ProcessedData['totals'] = {
    inbound: 0,
    outbound: 0,
    total: 0,
  };

  if (isMultiLocation) {
    // There will be multiple location objects with single/multiple dates
    groups = Object.entries(data?.aggregatedMultiLocations || {}).map(([key, values]) => {
      const incoming = values[dataKey]?.inbound || 0;
      const outgoing = values[dataKey]?.outbound || 0;
      const total = incoming + outgoing;

      grandTotals.inbound += incoming;
      grandTotals.outbound += outgoing;
      grandTotals.total += total;

      return {
        name: locations?.[key] || key,
        values: {
          inbound: incoming,
          outbound: outgoing,
          total,
        },
      };
    });
  } else {
    // For hourly data: There will be only one object with single date and hourly data for the given location
    // For daily data: There will be only one object with multiple dates for the given location
    const locationData = Object.values(data?.locations || {})[0] || {};

    if (isHourlyData) {
      // Hourly data is restricted to a single date, hence only one object
      const { inbound, outbound } = Object.values(locationData || {})[0] || {};
      const inboundHourly = inbound?.[dataKey]?.hourly || [];
      const outboundHourly = outbound?.[dataKey]?.hourly || [];
      const totalHourly = inboundHourly.map((value, i) => value + (outboundHourly[i] || 0));

      groups = totalHourly.map((total, i) => {
        const inbound = inboundHourly[i] || 0;
        const outbound = outboundHourly[i] || 0;

        grandTotals.inbound += inbound;
        grandTotals.outbound += outbound;
        grandTotals.total += total;

        return {
          name: getHourlyInsightsLabel(i),
          values: {
            inbound,
            outbound,
            total,
          },
        };
      });
    } else {
      groups = Object.entries(locationData).map(([date, { inbound, outbound }]) => {
        const incoming = inbound?.[dataKey]?.daily || 0;
        const outgoing = outbound?.[dataKey]?.daily || 0;
        const total = incoming + outgoing;

        grandTotals.inbound += incoming;
        grandTotals.outbound += outgoing;
        grandTotals.total += total;

        return {
          name: formatters.date(date),
          values: {
            inbound: incoming,
            outbound: outgoing,
            total,
          },
        };
      });
    }
  }

  return {
    chartData: {
      groups,
    },
    totals: grandTotals,
  };
};
